/**
 * SF-4576 — "External AI access (Claude, ChatGPT)" switch on the company
 * detail page (General Info tab).
 *
 * Lets staff turn the company's `mcp_access` flag on or off. Saved on its own,
 * straight away, like the CSAT custom sending domain toggle (CsatDomainTab):
 * the PATCH carries ONLY `mcp_access`, and the Edit form's payload builder
 * never includes it, so a normal company save can never flip it.
 *
 * Turning it OFF cuts every existing Claude/ChatGPT connection for the
 * company, so it asks first. sf-api cuts the connections BEFORE changing the
 * flag; if that fails it answers 502 MCP_REVOKE_FAILED and the flag stays on,
 * so we show the server's message and leave the switch on.
 *
 * Changing it needs admin:companies:write (the admin PATCH route's
 * permission). Without it the switch is disabled with a tooltip; while
 * permissions are still loading it is just disabled (no "denied" flash).
 * sf-api enforces the permission too; this is defence in depth.
 *
 * There is ONE source of truth for the saved value: the page's company query.
 * After a successful PATCH we hand the returned company to `onSaved`, which
 * writes it into that query's cache. So the switch shows the saved value even
 * if the refresh fails, and any later successful read (the page's Refresh,
 * refocus) simply replaces it. A local "confirmed" copy kept here used to go
 * stale: a later read returning the same cached value never cleared it.
 *
 * SF-4656: the switch now sits on the Feature Flags tab and follows the
 * page's one-save-at-a-time rule (companySaveStore): it is locked while ANY
 * save for this company runs (its own included, even after it remounted),
 * and a click while one runs is refused. The PATCH goes through
 * useCompanyPatch, which, unlike useUpdate, never rolls back other
 * companies' cache entries when it fails.
 */
import { useEffect, useRef, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import { Popconfirm, Space, Switch, Tooltip, Typography, message } from 'antd';
import { hasPermission } from '../../../utils/permissions';
import { useCanEditCompany } from '../api/editableCompaniesClient';
import type { Company } from '../list';
import { useCompanyPatch } from './useCompanyPatch';
import {
  beginCompanySave,
  endCompanySave,
  updateCompanySave,
  useRunningCompanySave,
} from './companySaveStore';
import { SAVE_REFUSED_TEXT } from './useSaveCompanyField';

const { Text } = Typography;

const WRITE_PERMISSION = 'admin:companies:write';

export interface McpAccessSwitchProps {
  companyId: string;
  /** Current saved value from the detail page's company query. */
  enabled: boolean;
  /**
   * Called with the company the PATCH returned (its `mcp_access` is the value
   * the server confirmed). The page writes it into its company query cache.
   */
  onSaved: (saved: Company) => void;
  /**
   * Re-runs the detail page's company query. TanStack resolves (does not
   * throw) when the GET fails, so the result's `isError` says whether fresh
   * data actually arrived.
   */
  refetchCompany: () => Promise<{ isError?: boolean }>;
  /** SF-4656 — named in messages that arrive after staff moved to another company. */
  companyName?: string;
}

type SaveError = {
  response?: { status?: number; data?: { reason_code?: string; message?: string } };
};

export const McpAccessSwitch: React.FC<McpAccessSwitchProps> = ({
  companyId,
  enabled,
  onSaved,
  refetchCompany,
  companyName,
}) => {
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  // SF-4668: staff with a company list may only change the companies on it.
  const { canEditCompany, isLoading: scopeLoading } = useCanEditCompany(companyId);
  const canWrite = !permsLoading && hasPermission(permissions ?? [], WRITE_PERMISSION) && canEditCompany;

  const patchCompany = useCompanyPatch();
  // Any save running for this company (this switch's or another control's).
  const running = useRunningCompanySave(companyId);
  const busy = running !== null;
  // Where this switch's own save is heading, while it runs.
  const pending =
    running?.kind === 'field' && running.field === 'mcp_access' ? (running.value as boolean) : null;
  const current = enabled;

  // Messages that arrive after staff left this company say which company.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const say = (text: string) =>
    mounted.current ? text : `${companyName ?? 'Another company'}: ${text}`;

  const save = async (next: boolean) => {
    const token = beginCompanySave(companyId, { kind: 'field', field: 'mcp_access', value: next });
    if (token === null) {
      message.warning(SAVE_REFUSED_TEXT);
      return;
    }
    try {
      let saved: Company;
      try {
        saved = await patchCompany(companyId, { mcp_access: next });
      } catch (err) {
        const res = (err as SaveError)?.response;
        // Connections could not be cut, so sf-api left the flag on: say why.
        if (res?.status === 502 && res.data?.reason_code === 'MCP_REVOKE_FAILED') {
          message.error(
            say(res.data.message || 'Could not cut the existing connections. Access is still on.'),
          );
        } else {
          message.error(say('Could not save external AI access. Please try again.'));
        }
        // The switch falls back to the saved value: unchanged on any error.
        return;
      }
      // Saved. Put what the server confirmed (fall back to what we sent if the
      // response omits the field) into the page's company cache, so the switch
      // keeps it even if the refresh fails; a later successful read replaces it.
      const value = typeof saved?.mcp_access === 'boolean' ? saved.mcp_access : next;
      onSaved({ ...saved, mcp_access: value });
      updateCompanySave(companyId, token, value);
      message.success(say(value ? 'External AI access turned on' : 'External AI access turned off'));
      const refreshed = await refetchCompany().catch(() => ({ isError: true }));
      if (refreshed?.isError) message.warning(say('Saved, but the page could not refresh'));
    } finally {
      endCompanySave(companyId, token);
    }
  };

  const disabled = !canWrite || busy;
  // A confirmation left open when another save starts closes, and does
  // nothing while locked (review finding).
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => {
    if (disabled) setConfirmOpen(false);
  }, [disabled]);
  const control = (
    <Popconfirm
      title="Turn off external AI access?"
      description="Every existing Claude and ChatGPT connection for this company will be cut."
      okText="Turn off"
      okButtonProps={{ danger: true }}
      open={confirmOpen && !disabled}
      onOpenChange={(next) => setConfirmOpen(next && !disabled)}
      onConfirm={() => {
        setConfirmOpen(false);
        if (!disabled) void save(false);
      }}
      // Only turning OFF asks first; turning ON saves straight from onChange.
      disabled={!current || disabled}
    >
      <Switch
        checked={pending ?? current}
        loading={busy || permsLoading || scopeLoading}
        disabled={disabled}
        onChange={current ? undefined : () => void save(true)}
        data-testid="mcp-access-toggle"
        aria-label="External AI access (Claude, ChatGPT)"
      />
    </Popconfirm>
  );

  return (
    <Space align="start">
      {!permsLoading && !scopeLoading && !canWrite ? (
        <Tooltip
          title={
            canEditCompany
              ? 'Changing this needs company edit rights (admin:companies:write).'
              : 'This company is not on your list of companies, so you can only view it.'
          }
        >
          {/* span: a disabled Switch swallows the hover a Tooltip needs. */}
          <span data-testid="mcp-access-readonly">{control}</span>
        </Tooltip>
      ) : (
        control
      )}
      <Text type="secondary">
        Lets this company&apos;s users connect Claude or ChatGPT to read their ServeFirst data. Off
        by default.
      </Text>
    </Space>
  );
};
